/* =============================================================
   THE BANDS

   One definition of "doing well" and "needs looking at", shared by
   the administrator's comparison box and the trainer's own summary.

   Shared deliberately. If each computed its own verdict, the day
   would come when an administrator's dashboard says a trainer is at
   risk and that trainer's own dashboard says they are fine — and
   the trainer would find out which was right in a conversation
   nobody wanted to have. Two views of one fact must be one fact.
   ============================================================= */

/** Below this an average is one or two opinions, not a verdict. */
export const MIN_RESPONSES = 3;

/** At or below this average, a trainer needs looking at. */
export const AT_RISK_AVERAGE = 3.2;

/** Or this share of ratings at 2 and below, whatever the average. */
export const AT_RISK_LOW_SHARE = 25;

/** Clear of the risk band and comfortably above it. */
export const DOING_WELL_AVERAGE = 4.2;

/**
 * Which band a set of figures falls in.
 *
 * 'quiet'  — not enough responses to say anything. A distinct answer
 *            rather than a good or bad one: silence is not praise, and
 *            calling it praise is how a report loses its meaning.
 * 'risk'   — at or under the average threshold, or carrying too many
 *            poor ratings.
 * 'good'   — clear of the risk band and above the good threshold.
 * 'steady' — in between. Fine, and not worth a colour.
 */
export const bandFor = ({ averageRating = 0, lowCount = 0, responses = 0 } = {}) => {
  if (!responses || responses < MIN_RESPONSES) return 'quiet';

  const lowShare = responses ? (lowCount / responses) * 100 : 0;
  if (averageRating <= AT_RISK_AVERAGE || lowShare >= AT_RISK_LOW_SHARE) return 'risk';
  if (averageRating >= DOING_WELL_AVERAGE) return 'good';
  return 'steady';
};

/** What each band is called, and what it means, in the reader's own terms. */
export const BAND_COPY = {
  quiet: {
    label: 'Not enough yet',
    line: `Ratings start counting once ${MIN_RESPONSES} students have left one.`,
  },
  risk: {
    label: 'Needs looking at',
    line: 'Worth reading the low ratings and what was said with them.',
  },
  good: {
    label: 'Doing well',
    line: 'Students are rating these sessions highly.',
  },
  steady: {
    label: 'Steady',
    line: 'Nothing standing out either way.',
  },
};

/* The three tones a rating carries. MEANING, so literal: a palette able
   to render "2 out of 5" in the same colour as "5 out of 5" would leave
   every chart drawn from these saying nothing. */
export const RATING_TONE = {
  happy: '#0E7C6B',
  neutral: '#F5A524',
  unhappy: '#C0392B',
};

/**
 * The summary's rating groups, coloured for the ring.
 *
 * The grouping (happy / neither / unhappy), the counts and each share are
 * worked out by the API -- /studentfeedbacks/summary returns them as
 * `ratingGroups`. This only gives each group its tone.
 */
export const ratingSegments = (groups = []) =>
  (Array.isArray(groups) ? groups : []).map((g) => ({
    label: g.label,
    value: Number(g.value) || 0,
    share: g.share,
    color: RATING_TONE[g.key] || RATING_TONE.neutral,
  }));
